import type {
  BudgetHealth,
  CampaignAiRun,
  CampaignDetailCampaign,
  MetricSnapshot,
  PlacementBreakdown,
} from "@adpilot/shared";
import { resolveExecutionModeV1 } from "@adpilot/shared";
import {
  IconBrain,
  IconChartLine,
  IconChevronRight,
  IconDatabaseOff,
  IconEdit,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";
import { CampaignAdGroups } from "@/components/campaign/ad-groups/CampaignAdGroups";
import { campaignAdGroupsQuery } from "@/components/campaign/ad-groups/campaign-ad-groups-query";
import { CampaignAiRuns } from "@/components/campaign/ai-runs/CampaignAiRuns";
import { CampaignBidAdjustment } from "@/components/campaign/bid-adjustment/CampaignBidAdjustment";
import { CampaignBudgetPanel } from "@/components/campaign/budget/CampaignBudgetPanel";
import { CampaignAiActionDialog } from "@/components/campaign/CampaignAiActionDialog";
import { CampaignStateMenu } from "@/components/campaign/CampaignStateMenu";
import {
  CampaignDetailHeading,
  CampaignDetailNavigation,
} from "@/components/campaign/campaign-detail-shell";
import { CampaignEditDrawer } from "@/components/campaign/campaign-edit-drawer";
import { CampaignPolicyDialog } from "@/components/campaign/campaign-policy-dialog";
import { CampaignChangeHistory } from "@/components/campaign/change-history/CampaignChangeHistory";
import { CampaignNegatives } from "@/components/campaign/negatives/CampaignNegatives";
import { CampaignDetailPerformance } from "@/components/campaign/performance/CampaignDetailPerformance";
import { CampaignSearchTerms } from "@/components/campaign/search-terms/CampaignSearchTerms";
import { labelize } from "@/components/campaign/targeting-tables";
import { CampaignTargets } from "@/components/campaign/targets/CampaignTargets";
import {
  campaignNegativesQuery,
  campaignSearchTermsQuery,
  campaignTargetsQuery,
} from "@/components/campaign/targets/campaign-targets-query";
import {
  DataGridBody,
  DataGridHeader,
  dataGridRowClassName,
} from "@/components/patterns/DataTable";
import { ReportingFilters } from "@/components/patterns/reporting-filters";
import { BudgetHealthBadge } from "@/components/shared/BudgetHealthBadge";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { TabsContent } from "@/components/ui/tabs";
import { usePermission } from "@/hooks/use-permission";
import { DEFAULT_APPROVAL_SEARCH } from "@/lib/approval-search";
import { fetchCampaignDetail } from "@/lib/campaign-api";
import { useFormatters } from "@/lib/format";
import { requireModule } from "@/lib/module-access";
import { cn } from "@/lib/utils";
import { type ReportingPeriod, useReportingPeriodStore } from "@/stores/reporting-period";
import { useReportingTimeZoneStore } from "@/stores/reporting-time-zone";

export const Route = createFileRoute("/_authed/campaigns/$id/")({
  beforeLoad: () => {
    requireModule("campaigns");
  },
  component: CampaignDetailPage,
});

function CampaignDetailPage() {
  const { t } = useTranslation("campaign");
  const { id } = Route.useParams();
  const range = useReportingPeriodStore((state) => state.period);
  const timeZone = useReportingTimeZoneStore((state) => state.timeZone);
  const { isAdmin } = usePermission();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["campaign", id, range, timeZone],
    queryFn: () => fetchCampaignDetail(id, range, timeZone),
  });

  const adGroupsQuery = useQuery({
    ...campaignAdGroupsQuery(id, range, timeZone),
    enabled: !!data?.connected && !!data.campaign,
  });
  const targetsQuery = useQuery({
    ...campaignTargetsQuery(id, range, timeZone),
    enabled: !!data?.connected && !!data.campaign,
  });
  const negativesQuery = useQuery({
    ...campaignNegativesQuery(id),
    enabled: !!data?.connected && !!data.campaign,
  });
  const searchTermsQuery = useQuery({
    ...campaignSearchTermsQuery(id, range, timeZone),
    enabled: !!data?.connected && !!data.campaign,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <CampaignSkeleton />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-6">
        <ErrorState message={t("loadFailed")} onRetry={() => void refetch()} />
      </div>
    );
  }

  if (!data.connected || !data.campaign) {
    return (
      <div className="space-y-6">
        <EmptyState
          icon={<IconDatabaseOff className="size-16" />}
          title={t("empty.campaign.title")}
          description={t("empty.campaign.description")}
        />
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-[18px] px-6 pb-[22px] pt-[18px]">
      <CampaignHeader campaign={data.campaign} />
      <CampaignDetailNavigation
        key={id}
        adGroupCount={
          adGroupsQuery.data?.connected ? adGroupsQuery.data.adGroups.length : undefined
        }
        targetCount={targetsQuery.data?.connected ? targetsQuery.data.targets.length : undefined}
        negativeCount={
          negativesQuery.data?.connected ? negativesQuery.data.negatives.length : undefined
        }
        searchTermCount={
          searchTermsQuery.data?.connected ? searchTermsQuery.data.searchTerms.length : undefined
        }
        runCount={data.aiRuns.length}
      >
        <TabsContent value="overview" className="flex flex-col gap-4">
          <CampaignSummary campaign={data.campaign} />
          <CampaignOverview
            campaign={data.campaign}
            campaignId={id}
            range={range}
            runs={data.aiRuns}
          />
        </TabsContent>
        <TabsContent value="ad-groups" className="flex flex-col gap-4">
          <CampaignAdGroups key={id} campaignId={id} range={range} timeZone={timeZone}>
            <CampaignDetailPerformance
              key={id}
              campaignId={id}
              shopId={data.campaign.shopId}
              range={range}
              timeZone={timeZone}
            />
          </CampaignAdGroups>
        </TabsContent>
        <TabsContent value="budget">
          <CampaignBudgetPanel key={id} campaign={data.campaign} canWrite={isAdmin} />
        </TabsContent>
        <TabsContent value="targets" className="flex flex-col gap-4">
          <CampaignTargets key={id} campaignId={id} range={range} timeZone={timeZone} />
        </TabsContent>
        <TabsContent value="negatives">
          <CampaignNegatives key={id} campaignId={id} />
        </TabsContent>
        <TabsContent value="search-terms">
          <CampaignSearchTerms key={id} campaignId={id} range={range} timeZone={timeZone} />
        </TabsContent>
        <TabsContent value="bid-adjustment">
          <CampaignBidAdjustment
            key={id}
            campaignId={id}
            shopId={data.campaign.shopId}
            campaignType={data.campaign.campaignType}
            range={range}
            timeZone={timeZone}
            canWrite={isAdmin}
          />
        </TabsContent>
        <TabsContent value="log">
          <CampaignChangeHistory
            key={id}
            campaignId={id}
            shopId={data.campaign.shopId}
            campaignType={data.campaign.campaignType}
            period={range}
            timeZone={timeZone}
          />
        </TabsContent>
        <TabsContent value="ai-runs">
          <CampaignAiRuns runs={data.aiRuns} actions={data.aiActions} />
        </TabsContent>
      </CampaignDetailNavigation>
    </div>
  );
}

function CampaignOverview({
  campaign,
  campaignId,
  range,
  runs,
}: {
  campaign: CampaignDetailCampaign;
  campaignId: string;
  range: ReportingPeriod;
  runs: CampaignAiRun[];
}) {
  const { t } = useTranslation("campaign");
  const { formatCurrency, formatPercent } = useFormatters();
  const timeZone = useReportingTimeZoneStore((state) => state.timeZone);
  const query = useQuery({
    ...campaignAdGroupsQuery(campaignId, range, timeZone),
  });
  const adGroups = query.data?.connected ? query.data.adGroups.slice(0, 3) : [];
  const latestRun = runs[0];

  return (
    <div className="grid min-h-[518px] grid-cols-[minmax(0,2fr)_minmax(330px,1fr)] gap-[14px]">
      <section className="overflow-hidden rounded-[14px] border border-line bg-surface">
        <div className="flex h-[42px] items-center justify-between border-b border-line px-3.5">
          <h2 className="text-[14px] font-semibold text-ink">
            {t("detail.adGroups.headingActive", { count: adGroups.length })}
          </h2>
          {adGroups[0] ? (
            <Link
              to="/campaigns/$id/ad-groups/$adGroupId"
              params={{ id: campaignId, adGroupId: adGroups[0].id }}
              className="flex items-center gap-1 text-[10px] font-medium text-ink"
            >
              {t("detail.adGroups.viewTargeting")} <IconChevronRight className="size-3" />
            </Link>
          ) : null}
        </div>
        <DataGridHeader className="grid-cols-[minmax(230px,2fr)_repeat(3,minmax(70px,1fr))_70px_90px]">
          <span>{t("detail.adGroups.table.adGroup")}</span>
          <span className="text-right">{t("detail.adGroups.table.spend")}</span>
          <span className="text-right">{t("detail.adGroups.table.sales")}</span>
          <span className="text-right">{t("detail.adGroups.table.acos")}</span>
          <span className="text-right">{t("detail.adGroups.table.targets")}</span>
          <span className="text-right">{t("detail.adGroups.table.state")}</span>
        </DataGridHeader>
        {query.isLoading ? (
          <div className="space-y-2 p-3.5">
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
          </div>
        ) : adGroups.length === 0 ? (
          <div className="flex h-[174px] items-center justify-center text-[11px] text-muted-foreground">
            {t("detail.adGroups.emptyOverview")}
          </div>
        ) : (
          <DataGridBody>
            {adGroups.map((adGroup) => (
              <Link
                key={adGroup.id}
                to="/campaigns/$id/ad-groups/$adGroupId"
                params={{ id: campaignId, adGroupId: adGroup.id }}
                className={dataGridRowClassName(
                  "standard",
                  "grid-cols-[minmax(230px,2fr)_repeat(3,minmax(70px,1fr))_70px_90px]",
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate text-[11px] font-semibold text-ink">
                    {adGroup.name}
                  </span>
                  <span className="block truncate text-[9px] text-muted-foreground">
                    {t("detail.adGroups.targetsCount", { count: adGroup.targetCount })}
                  </span>
                </span>
                <span className="text-right tabular-nums">
                  {formatCurrency(adGroup.metrics.spend)}
                </span>
                <span className="text-right tabular-nums">
                  {formatCurrency(adGroup.metrics.sales)}
                </span>
                <span className="text-right font-semibold tabular-nums">
                  {formatPercent(adGroup.metrics.acos)}
                </span>
                <span className="text-right tabular-nums">{adGroup.targetCount}</span>
                <span
                  className={cn(
                    "text-right",
                    adGroup.aiManaged ? "text-green-11" : "text-muted-foreground",
                  )}
                >
                  {adGroup.aiManaged
                    ? t("detail.adGroups.aiState.managed")
                    : campaign.aiManaged
                      ? t("detail.adGroups.aiState.observe")
                      : t("detail.adGroups.aiState.notEnabled")}
                </span>
              </Link>
            ))}
          </DataGridBody>
        )}
        <div className="px-3.5 py-4">
          <h3 className="mb-2 text-[11px] font-semibold text-ink">{t("detail.placement.title")}</h3>
          {campaign.placements.slice(0, 3).map((placement, index) => (
            <div
              key={placement.placement}
              className="grid h-[22px] grid-cols-[122px_minmax(0,1fr)_72px] items-center gap-2 text-[9px]"
            >
              <span className="truncate capitalize text-ink">
                {placement.placement.replaceAll("_", " ")}
              </span>
              <span className="h-1.5 overflow-hidden rounded-full bg-subtle">
                <span
                  className={cn(
                    "block h-full rounded-full",
                    index === 0 ? "bg-ink" : "bg-muted-foreground/45",
                  )}
                  style={{ width: `${Math.max(8, placement.spendShare * 100)}%` }}
                />
              </span>
              <span className="text-right font-semibold tabular-nums">
                {t("detail.placement.acosValue", { value: formatPercent(placement.acos) })}
              </span>
            </div>
          ))}
        </div>
      </section>

      <section className="overflow-hidden rounded-[14px] border border-line bg-surface">
        <div className="flex h-[64px] items-start justify-between border-b border-line px-3.5 py-3">
          <div>
            <h2 className="text-[14px] font-semibold text-ink">{t("detail.latestRun.title")}</h2>
            <p className="mt-0.5 text-[9px] text-muted-foreground">
              {latestRun ? t("detail.latestRun.subtitle") : t("detail.latestRun.noHistory")}
            </p>
          </div>
          {latestRun ? (
            <span
              className={cn(
                "rounded-full px-2 py-1 text-[9px]",
                latestRun.status === "suspended"
                  ? "bg-amber/5 text-amber-11"
                  : "bg-green/5 text-green-11",
              )}
            >
              {latestRun.status === "suspended"
                ? t("detail.latestRun.decisionNeeded")
                : labelize(latestRun.status)}
            </span>
          ) : null}
        </div>
        {latestRun ? (
          <div className="flex h-[calc(100%-64px)] flex-col p-3.5">
            <p className="text-[11px] font-semibold leading-5 text-ink">
              {t("detail.latestRun.summary")}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <RunFact
                label={t("detail.latestRun.bidChanges")}
                value={String(latestRun.candidateActions)}
              />
              <RunFact
                label={t("detail.latestRun.executed")}
                value={String(latestRun.autoExecuted)}
              />
            </div>
            <p className="mt-3 text-[9px] leading-4 text-muted-foreground">
              {t("detail.latestRun.evidence")}
            </p>
            <Button
              className="mt-3 h-8 w-full rounded-[8px] text-[10px]"
              render={<Link to="/ai-operations" search={DEFAULT_APPROVAL_SEARCH} />}
            >
              {t("detail.latestRun.reviewPlan")}
            </Button>
          </div>
        ) : (
          <div className="flex h-[calc(100%-64px)] flex-col items-center justify-center px-8 text-center">
            <span className="flex size-9 items-center justify-center rounded-full border border-line bg-subtle">
              <IconBrain className="size-4" />
            </span>
            <p className="mt-3 text-[12px] font-semibold text-ink">
              {t("detail.latestRun.emptyTitle")}
            </p>
            <p className="mt-2 text-[10px] leading-4 text-muted-foreground">
              {t("detail.latestRun.emptyDescription")}
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

function RunFact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] text-muted-foreground">{label}</p>
      <p className="mt-1 font-mono text-sm font-semibold tabular-nums text-ink">{value}</p>
    </div>
  );
}

function CampaignHeader({ campaign }: { campaign: CampaignDetailCampaign }) {
  const { t } = useTranslation(["campaign", "smartCreation"]);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const { isAdmin, hasModule, hasPermission } = usePermission();

  return (
    <>
      <CampaignDetailHeading
        filters={<ReportingFilters scope="resource" />}
        campaign={campaign}
        breadcrumb={
          <nav
            aria-label={t("detail.breadcrumb")}
            className="flex items-center gap-1.5 text-xs text-muted-foreground"
          >
            <Link
              to="/campaigns"
              className="rounded-sm hover:text-ink focus-visible:outline-2 focus-visible:outline-ring"
            >
              {t("detail.breadcrumb")}
            </Link>
            <IconChevronRight className="size-3.5 shrink-0" aria-hidden />
            <span className="min-w-0 truncate" title={campaign.name}>
              {campaign.name}
            </span>
          </nav>
        }
        actions={
          <>
            <CampaignStateMenu campaign={campaign} canWrite={isAdmin} />
            <CampaignEditDrawer campaign={campaign} canWrite={isAdmin} />
            {campaign.campaignType === "sp" && campaign.shopId ? (
              <Button
                variant="outline"
                size="sm"
                disabled={!hasPermission("manage_ai")}
                onClick={() => setActionsOpen(true)}
              >
                {t("smartCreation:review.aiActions.title")}
              </Button>
            ) : null}
            {campaign.managedGroup ? (
              <Button
                variant="outline"
                size="sm"
                disabled={!hasModule("ai")}
                render={<Link to="/managed-groups/$id" params={{ id: campaign.managedGroup.id }} />}
              >
                {t("detail.policy.openManagedGroup")}
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                disabled={!hasPermission("manage_ai")}
                onClick={() => setPolicyOpen(true)}
              >
                <IconEdit data-icon="inline-start" />
                {t("detail.policy.editSettings")}
              </Button>
            )}
          </>
        }
      />
      <CampaignPolicyDialog
        campaignId={campaign.id}
        campaignName={campaign.name}
        open={policyOpen}
        onOpenChange={setPolicyOpen}
      />
      {campaign.shopId ? (
        <CampaignAiActionDialog
          campaignId={campaign.id}
          shopId={campaign.shopId}
          campaignName={campaign.name}
          canManageAi={hasPermission("manage_ai")}
          open={actionsOpen}
          onOpenChange={setActionsOpen}
        />
      ) : null}
    </>
  );
}

function CampaignSummary({ campaign }: { campaign: CampaignDetailCampaign }) {
  const { t } = useTranslation("campaign");
  const { formatCurrency, formatPercent, formatNumber } = useFormatters();
  const [policyOpen, setPolicyOpen] = useState(false);
  const { hasPermission } = usePermission();
  const policy = campaign.aiPolicySummary;
  const hasCampaignOverride =
    !campaign.managedGroup &&
    (policy?.businessGoalSource === "campaign" ||
      policy?.targetAcosSource === "campaign" ||
      policy?.approvalRiskThresholdSource === "campaign" ||
      policy?.dryRunSource === "campaign");

  return (
    <>
      <div className="grid h-[124px] shrink-0 grid-cols-[minmax(0,2fr)_minmax(330px,1fr)] gap-[14px]">
        <section className="overflow-hidden rounded-[14px] border border-line bg-surface">
          <div className="flex h-[34px] items-center justify-between border-b border-line bg-subtle px-3.5 text-[9px] font-medium uppercase tracking-[0.04em] text-muted-foreground">
            <span>{t("detail.performance.title")}</span>
            {/* ADP-89: derived budget-health estimate (today's spend vs daily budget). */}
            <BudgetHealthBadge
              health={campaign.budgetHealth}
              className="normal-case tracking-normal"
            />
          </div>
          <div className="grid h-[89px] grid-cols-5 divide-x divide-line">
            {[
              [t("detail.performance.spend"), formatCurrency(campaign.metrics.spend)],
              [t("detail.performance.adSales"), formatCurrency(campaign.metrics.sales)],
              [t("detail.performance.acos"), formatPercent(campaign.metrics.acos)],
              [t("detail.performance.orders"), formatNumber(campaign.metrics.orders)],
              [
                t("detail.performance.impressions"),
                formatNumber(campaign.metrics.impressions, { maximumFractionDigits: 0 }),
              ],
            ].map(([label, value]) => (
              <div key={label} className="flex flex-col justify-center px-4">
                <span className="text-[9px] uppercase text-muted-foreground">{label}</span>
                <span className="mt-1 text-[20px] font-semibold leading-6 tabular-nums text-ink">
                  {value}
                </span>
                <span className="mt-0.5 text-[9px] text-green-11">
                  {t("detail.performance.selectedPeriod")}
                </span>
              </div>
            ))}
          </div>
        </section>
        <section className="overflow-hidden rounded-[14px] border border-line bg-surface">
          <div
            className={cn(
              "flex h-10 items-center justify-between border-b border-line px-3.5 text-[9px] font-medium uppercase tracking-[0.04em]",
              hasCampaignOverride ? "text-amber-11" : "text-ink",
            )}
          >
            <div className="flex items-center gap-2">
              <span>
                {t("detail.policy.title")}
                {policy
                  ? ` · ${campaign.managedGroup?.name ?? (hasCampaignOverride ? t("detail.policy.campaignOverride") : t("detail.policy.shopPolicy"))}`
                  : ""}
              </span>
              <span
                className={cn(
                  "size-1.5 rounded-full",
                  policy?.effectiveEnabled ? "bg-green" : "bg-muted-foreground/40",
                )}
                aria-hidden
              />
            </div>
            {campaign.managedGroup ? (
              <Button
                variant="outline"
                className="h-7 rounded-[7px] px-2.5 text-[10px] font-medium normal-case tracking-normal"
                render={<Link to="/managed-groups/$id" params={{ id: campaign.managedGroup.id }} />}
              >
                {t("detail.policy.openManagedGroup")}
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="h-7 rounded-[7px] px-2.5 text-[10px] font-medium normal-case tracking-normal"
                disabled={!hasPermission("manage_ai")}
                onClick={() => setPolicyOpen(true)}
              >
                <IconEdit className="size-3" />
                {policy ? t("detail.policy.editSettings") : t("detail.setupAi")}
              </Button>
            )}
          </div>
          {policy ? (
            <div className="grid h-[83px] grid-cols-3 items-center px-3.5">
              <PolicyFact
                label={t("detail.policy.businessGoal")}
                value={businessGoalLabel(t, policy.businessGoal)}
              />
              <PolicyFact
                label={t("detail.policy.targetAcos")}
                value={formatPercent(policy.targetAcos)}
              />
              <PolicyFact
                label={t("detail.policy.mode")}
                value={executionModeLabel(t, policy.dryRun, policy.approvalRiskThreshold)}
              />
            </div>
          ) : (
            <div className="flex h-[83px] items-center gap-3 px-4">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-[8px] border border-line bg-subtle">
                <IconBrain className="size-4" />
              </span>
              <div>
                <p className="text-[11px] font-semibold text-ink">
                  {t("detail.policy.waitingTitle")}
                </p>
                <p className="mt-0.5 text-[9px] leading-4 text-muted-foreground">
                  {t("detail.policy.waitingDescription")}
                </p>
              </div>
            </div>
          )}
        </section>
      </div>

      <CampaignPolicyDialog
        campaignId={campaign.id}
        campaignName={campaign.name}
        open={policyOpen}
        onOpenChange={setPolicyOpen}
      />
    </>
  );
}

function PolicyFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-[8px] uppercase text-muted-foreground">{label}</p>
      <p className="mt-1 truncate text-[10px] font-semibold text-ink">{value}</p>
    </div>
  );
}

function businessGoalLabel(
  t: TFunction,
  goal: CampaignDetailCampaign["aiPolicySummary"] extends infer Summary
    ? Summary extends { businessGoal: infer Goal }
      ? Goal
      : never
    : never,
): string {
  return t(`detail.businessGoal.${goal}`);
}

function executionModeLabel(
  t: TFunction,
  dryRun: boolean,
  threshold: NonNullable<CampaignDetailCampaign["aiPolicySummary"]>["approvalRiskThreshold"],
): string {
  const mode = resolveExecutionModeV1({ dryRun, approvalRiskThreshold: threshold });
  if (mode === "recommendation") return t("detail.executionMode.recommendation");
  return mode === "full-auto"
    ? t("detail.executionMode.fullAuto")
    : t("detail.executionMode.semiAuto");
}

// biome-ignore lint/correctness/noUnusedVariables: Retained until the legacy Campaign policy route is removed.
function StrategyCard({
  summary,
  lastAiRun,
  pendingApprovals,
}: {
  summary: NonNullable<CampaignDetailCampaign["aiPolicySummary"]>;
  lastAiRun: string | null;
  pendingApprovals: number;
}) {
  const { t } = useTranslation("campaign");
  const { formatDateTime } = useFormatters();
  const on = t("strategy.on");
  const off = t("strategy.off");
  const items = [
    [t("strategy.effectiveAi"), summary.effectiveEnabled ? on : off],
    [
      t("strategy.approvalThreshold"),
      `${labelize(summary.approvalRiskThreshold)} · ${summary.approvalRiskThresholdSource}`,
    ],
    [t("strategy.dryRun"), `${summary.dryRun ? on : off} · ${summary.dryRunSource}`],
    [t("strategy.lastAiRun"), lastAiRun ? formatDateTime(lastAiRun) : t("strategy.never")],
    [t("strategy.pendingApprovals"), pendingApprovals.toString()],
  ];

  return (
    <Card className="rounded-[10px] border-line shadow-none">
      <CardHeader className="flex h-[48px] justify-center border-b border-line px-4 py-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <IconBrain className="size-4 text-primary" />
          {t("strategy.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-4">
          {items.map(([label, value]) => (
            <div key={label} className="flex flex-col gap-1">
              <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
              <dd className="text-sm font-medium text-foreground">{value}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

// biome-ignore lint/correctness/noUnusedVariables: Retained until the legacy Campaign summary route is removed.
function KpiCardRow({ metrics }: { metrics: MetricSnapshot }) {
  const { t } = useTranslation("campaign");
  const { formatCurrency, formatPercent, formatNumber } = useFormatters();
  // TODO(comparison): directional color + arrow when period-over-period lands
  // (prior-period isn't on the response yet — faking a direction would mislead).
  const cards: Array<{ label: string; value: string }> = [
    { label: t("metrics.spend"), value: formatCurrency(metrics.spend) },
    { label: t("metrics.sales"), value: formatCurrency(metrics.sales) },
    { label: t("metrics.acos"), value: formatPercent(metrics.acos) },
    { label: t("metrics.orders"), value: formatNumber(metrics.orders) },
  ];
  return (
    <div className="grid h-[78px] grid-cols-4 divide-x overflow-hidden rounded-[10px] border border-line bg-surface">
      {cards.map((card) => (
        <div key={card.label} className="flex flex-col justify-center px-4">
          <p className="text-[11px] font-medium text-muted-foreground">{card.label}</p>
          <p className="mt-1 font-mono text-[19px] font-semibold tabular-nums text-ink">
            {card.value}
          </p>
        </div>
      ))}
    </div>
  );
}

// biome-ignore lint/correctness/noUnusedVariables: Retained until the legacy Campaign budget route is removed.
function BudgetPacing({
  dailyBudget,
  todaySpend,
  budgetHealth,
}: {
  dailyBudget: number | null;
  todaySpend: number;
  budgetHealth: BudgetHealth;
}) {
  const { t } = useTranslation("campaign");
  const { formatCurrency } = useFormatters();
  if (dailyBudget == null) {
    return (
      <Card className="rounded-md">
        <CardContent className="flex items-center justify-between gap-2 p-4 text-sm text-muted-foreground">
          <span>{t("budget.noBudget")}</span>
          {/* ADP-89: still show the health reading (state=unknown → no_budget). */}
          <BudgetHealthBadge health={budgetHealth} />
        </CardContent>
      </Card>
    );
  }

  const ratio = dailyBudget > 0 ? todaySpend / dailyBudget : 0;
  const overPace = todaySpend > dailyBudget;

  return (
    <Card className="rounded-md">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2 font-medium text-foreground">
            {t("budget.pacingToday")}
            {/* ADP-89: derived budget-health estimate alongside raw pacing. */}
            <BudgetHealthBadge health={budgetHealth} />
          </span>
          <span className="font-mono tabular-nums text-muted-foreground">
            {formatCurrency(todaySpend)} / {formatCurrency(dailyBudget)}
          </span>
        </div>
        {todaySpend === 0 ? (
          <p className="text-xs text-muted-foreground">{t("budget.noSpendToday")}</p>
        ) : (
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn("h-full rounded-full", overPace ? "bg-destructive" : "bg-primary")}
              style={{ width: `${Math.min(ratio, 1) * 100}%` }}
            />
          </div>
        )}
        {overPace ? (
          <p className="text-xs font-medium text-destructive-foreground">
            {t("budget.overBy", { amount: formatCurrency(todaySpend - dailyBudget) })}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * ADP-49: placement breakdown (Top of Search / Rest of Search / Product Pages).
 * Campaign×placement grain (Amazon doesn't expose ad-group×placement) → lives on
 * the campaign overview. Horizontal bar = spend-share; tooltip carries the full
 * spend/sales/ACOS/share. Uses the shadcn chart layer (+ `--color-chart-*`).
 */
// biome-ignore lint/correctness/noUnusedVariables: Retained until the legacy placement route is removed.
function PlacementsSection({ placements }: { placements: PlacementBreakdown[] }) {
  const { t } = useTranslation("campaign");
  const { formatCurrency, formatPercent } = useFormatters();
  const placementLabel = (placement: string) =>
    t(`placements.labels.${placement}`, { defaultValue: placement });

  if (placements.length === 0) {
    return (
      <section className="space-y-3">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <IconChartLine className="size-5 text-primary" />
          {t("placements.title")}
        </h2>
        <EmptyState
          icon={<IconDatabaseOff className="size-12" />}
          title={t("placements.emptyTitle")}
          description={t("placements.emptyDescription")}
        />
      </section>
    );
  }

  const data = placements.map((p, i) => ({
    placement: placementLabel(p.placement),
    share: Number((p.spendShare * 100).toFixed(1)),
    color: `var(--color-chart-${(i % 3) + 1})`,
    raw: p,
  }));
  const chartConfig = {
    share: { label: t("placements.spendShare"), color: "var(--color-chart-1)" },
  } satisfies ChartConfig;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <IconChartLine className="size-5 text-primary" />
          {t("placements.title")}
        </h2>
        <span className="text-sm text-muted-foreground">
          {t("placements.count", { count: placements.length })}
        </span>
      </div>
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_240px]">
        <ChartContainer config={chartConfig} className="h-[200px] w-full">
          <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16 }}>
            <CartesianGrid horizontal={false} stroke="var(--border)" strokeDasharray="3 3" />
            <XAxis
              type="number"
              domain={[0, 100]}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 12 }}
              tickFormatter={(v: number) => `${v}%`}
            />
            <YAxis
              type="category"
              dataKey="placement"
              tickLine={false}
              axisLine={false}
              width={110}
              tick={{ fontSize: 12 }}
            />
            <ChartTooltip
              cursor={{ fill: "var(--muted)" }}
              content={
                <ChartTooltipContent
                  formatter={(value, _name, item) => {
                    const raw = (item.payload as { raw: PlacementBreakdown }).raw;
                    return (
                      <div className="space-y-0.5 text-xs">
                        <div className="font-medium text-foreground">
                          {placementLabel(raw.placement)}
                        </div>
                        <div className="font-mono tabular-nums text-muted-foreground">
                          {t("placements.tooltipVolume", {
                            spend: formatCurrency(raw.spend),
                            sales: formatCurrency(raw.sales),
                          })}
                        </div>
                        <div className="font-mono tabular-nums text-muted-foreground">
                          {t("placements.tooltipEfficiency", {
                            acos: formatPercent(raw.acos),
                            share: formatPercent(raw.spendShare),
                          })}
                        </div>
                        <div className="font-mono tabular-nums text-foreground">
                          {String(value)}%
                        </div>
                      </div>
                    );
                  }}
                />
              }
            />
            <Bar dataKey="share" radius={[0, 4, 4, 0]} maxBarSize={24}>
              {data.map((d) => (
                <Cell key={d.placement} fill={d.color} />
              ))}
            </Bar>
          </BarChart>
        </ChartContainer>
        <div className="space-y-2">
          {placements.map((p, i) => (
            <div
              key={p.placement}
              className="flex items-center justify-between rounded-md border border-border bg-card p-3"
            >
              <div className="flex items-center gap-2">
                <span
                  className="size-2 rounded-full"
                  style={{ backgroundColor: `var(--color-chart-${(i % 3) + 1})` }}
                />
                <span className="text-sm text-foreground">{placementLabel(p.placement)}</span>
              </div>
              <span className="font-mono text-sm tabular-nums text-muted-foreground">
                {formatPercent(p.spendShare)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CampaignSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-72" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="h-9 w-40" />
      </div>
      <Card className="rounded-md">
        <CardContent className="grid grid-cols-4 gap-4 pt-6">
          {["a", "b", "c", "d", "e", "f", "g", "h"].map((key) => (
            <div key={key} className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-5 w-32" />
            </div>
          ))}
        </CardContent>
      </Card>
      <Skeleton className="h-80 w-full" />
      <div className="rounded-md border border-border p-3">
        {["a", "b", "c", "d", "e", "f", "g", "h"].map((key) => (
          <Skeleton key={key} className="mb-3 h-9 w-full last:mb-0" />
        ))}
      </div>
    </div>
  );
}
